import React from 'react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#06B6D4', '#F97316'
];

const dollarFormatter = (v) => {
  if (typeof v !== 'number') return v;
  return v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v.toFixed(0)}`;
};

const numberFormatter = (v) => {
  if (typeof v !== 'number') return v;
  return v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v.toFixed(0);
};

const ChatChart = ({ chart }) => {
  const { type, title, xKey, series, data, valuePrefix } = chart;
  const isDollar = valuePrefix === '$';
  const fmt = isDollar ? dollarFormatter : numberFormatter;

  const tooltipFormatter = (value, name) => {
    const label = series?.find(s => s.key === name)?.label || name;
    const formatted = isDollar ? `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : value.toLocaleString();
    return [formatted, label];
  };

  if (!data || data.length === 0) return null;

  const chartContent = () => {
    switch (type) {
      case 'bar':
        return (
          <BarChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey={xKey} tick={{ fontSize: 10 }} interval="preserveStartEnd" />
            <YAxis tickFormatter={fmt} tick={{ fontSize: 10 }} width={45} />
            <Tooltip formatter={tooltipFormatter} />
            {series?.length > 1 && <Legend wrapperStyle={{ fontSize: 10 }} />}
            {(series || []).map((s, i) => (
              <Bar key={s.key} dataKey={s.key} name={s.key} fill={s.color || COLORS[i % COLORS.length]} radius={[2, 2, 0, 0]} />
            ))}
          </BarChart>
        );

      case 'line':
        return (
          <LineChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey={xKey} tick={{ fontSize: 10 }} interval="preserveStartEnd" />
            <YAxis tickFormatter={fmt} tick={{ fontSize: 10 }} width={45} />
            <Tooltip formatter={tooltipFormatter} />
            {series?.length > 1 && <Legend wrapperStyle={{ fontSize: 10 }} />}
            {(series || []).map((s, i) => (
              <Line key={s.key} type="monotone" dataKey={s.key} name={s.key} stroke={s.color || COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 2 }} />
            ))}
          </LineChart>
        );

      case 'area':
        return (
          <AreaChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey={xKey} tick={{ fontSize: 10 }} interval="preserveStartEnd" />
            <YAxis tickFormatter={fmt} tick={{ fontSize: 10 }} width={45} />
            <Tooltip formatter={tooltipFormatter} />
            {series?.length > 1 && <Legend wrapperStyle={{ fontSize: 10 }} />}
            {(series || []).map((s, i) => (
              <Area key={s.key} type="monotone" dataKey={s.key} name={s.key} stroke={s.color || COLORS[i % COLORS.length]} fill={s.color || COLORS[i % COLORS.length]} fillOpacity={0.15} strokeWidth={2} />
            ))}
          </AreaChart>
        );

      case 'pie':
        return (
          <PieChart margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={60}
              label={({ name, value }) => `${name}: ${isDollar ? '$' : ''}${value.toLocaleString()}`}
              labelLine={{ strokeWidth: 1 }}
              fontSize={9}
            >
              {data.map((_, i) => (
                <Cell key={i} fill={data[i].color || COLORS[i % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip formatter={(value) => isDollar ? `$${value.toLocaleString()}` : value.toLocaleString()} />
          </PieChart>
        );

      default:
        return null;
    }
  };

  return (
    <div className="my-2 bg-white rounded-md border border-gray-200 p-2">
      {title && <p className="text-xs font-semibold text-gray-700 mb-1 text-center">{title}</p>}
      <ResponsiveContainer width="100%" height={180}>
        {chartContent()}
      </ResponsiveContainer>
    </div>
  );
};

/**
 * Parse a message string and split it into text segments and chart objects.
 * Charts are delimited by |||chart ... ||| markers.
 */
export function parseMessageWithCharts(content) {
  const parts = [];
  const regex = /\|\|\|chart\s*([\s\S]*?)\|\|\|/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(content)) !== null) {
    // Text before the chart
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: content.slice(lastIndex, match.index).trim() });
    }
    // Parse the chart JSON
    try {
      const chart = JSON.parse(match[1].trim());
      parts.push({ type: 'chart', chart });
    } catch {
      // If JSON is invalid, treat as text
      parts.push({ type: 'text', content: match[0] });
    }
    lastIndex = match.index + match[0].length;
  }

  // Remaining text after last chart
  const remaining = content.slice(lastIndex).trim();
  if (remaining) {
    parts.push({ type: 'text', content: remaining });
  }

  return parts;
}

export default ChatChart;
